import React, {useMemo, useRef, useEffect, useState} from 'react'
import {useQueryClient, useMutation} from 'react-query'
import {QUERIES} from '../../../../../../_metronic/helpers'
import {useListView} from '../../core/ListViewProvider'
import {useQueryResponse, useQueryResponseData} from '../../core/QueryResponseProvider'
import {updateBulkUserStatus} from '../../core/_requests'
import toaster from '../../../../../../Utils/toaster'
import {BulkStatusConfirmationModal} from '../BulkStatusConfirmationModal'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import {User} from '../../core/_models'
import './style.css'

const isUserInactive = (user: User): boolean => {
  return (
    user.accountStatus === 'Not Active' ||
    user.lockoutEnabled === true ||
    (Boolean(user.email) && Boolean(user.isdeleted))
  )
}

const UsersListGrouping = () => {
  const {selected, clearSelected} = useListView()
  const queryClient = useQueryClient()
  const {query} = useQueryResponse()
  const usersData = useQueryResponseData()

  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<'activate' | 'inactivate'>('inactivate')
  const toggleRef = useRef<HTMLInputElement>(null)

  const selectedUsers = useMemo(() => {
    return (usersData || []).filter((u: User) => u.code && selected.includes(u.code))
  }, [usersData, selected])

  const {activeCount, inactiveCount} = useMemo(() => {
    let active = 0
    let inactive = 0
    for (const u of selectedUsers) {
      if (isUserInactive(u)) {
        inactive++
      } else {
        active++
      }
    }
    return {activeCount: active, inactiveCount: inactive}
  }, [selectedUsers])

  const allActive = selected.length > 0 && inactiveCount === 0
  const allInactive = selected.length > 0 && activeCount === 0
  const isMixed = !allActive && !allInactive

  // Determine target action when clicked:
  // If all active, toggle turns OFF -> inactivate.
  // If all inactive, toggle turns ON -> activate.
  // If mixed, direction is based on majority.
  const targetAction: 'activate' | 'inactivate' = useMemo(() => {
    if (allActive) return 'inactivate'
    if (allInactive) return 'activate'
    return activeCount >= inactiveCount ? 'inactivate' : 'activate'
  }, [allActive, allInactive, activeCount, inactiveCount])

  useEffect(() => {
    if (toggleRef.current) {
      toggleRef.current.indeterminate = isMixed
    }
  }, [isMixed])

  const bulkStatusMutation = useMutation(
    (isActive: boolean) => updateBulkUserStatus(selected, isActive),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([`${QUERIES.USERS_LIST}-${query}`])
        clearSelected()
        setConfirmModalOpen(false)
        toaster('success', data?.message || 'Status updated successfully')
      },
      onError: (error: Error) => {
        toaster('error', error?.message || 'Bulk status update failed')
      },
    }
  )

  const handleToggleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // Prevent default toggle change until confirmed in the modal
    e.preventDefault()
    setPendingAction(targetAction)
    setConfirmModalOpen(true)
  }

  const tooltipText = useMemo(() => {
    if (allActive) return 'Click to inactivate selected users'
    if (allInactive) return 'Click to activate selected users'
    return targetAction === 'inactivate'
      ? 'Click to inactivate all selected users'
      : 'Click to activate all selected users'
  }, [allActive, allInactive, targetAction])

  return (
    <div className='d-flex justify-content-end align-items-center'>
      <div className='fw-bolder me-3 ms-3'>
        <span className='badge badge-light-primary'>{selected.length} Selected</span>
      </div>

      <OverlayTrigger
        placement='top'
        overlay={<Tooltip id='tooltip-bulk-status'>{tooltipText}</Tooltip>}
      >
        <div className={`form-check form-switch form-check-custom form-check-solid bulk-status-switch ${isMixed ? 'is-indeterminate' : ''} me-2`}>
          <input
            ref={toggleRef}
            className='form-check-input cursor-pointer h-20px w-35px'
            type='checkbox'
            id='bulkStatusToggle'
            checked={allActive}
            onClick={handleToggleClick}
            readOnly
          />
          <label
            className='form-check-label fw-bold text-gray-700 fs-7 cursor-pointer user-select-none ms-2'
            htmlFor='bulkStatusToggle'
            onClick={handleToggleClick as any}
          >
            Status
          </label>
        </div>
      </OverlayTrigger>

      {isConfirmModalOpen && (
        <BulkStatusConfirmationModal
          action={pendingAction}
          count={selected.length}
          isLoading={bulkStatusMutation.isLoading}
          onConfirm={async () => {
            await bulkStatusMutation.mutateAsync(pendingAction === 'activate')
          }}
          onClose={() => setConfirmModalOpen(false)}
        />
      )}
    </div>
  )
}

export {UsersListGrouping}
